import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./KebabMenu.css";

export default function KebabMenu({
  open,
  onClose,
  actions = [],
  onAction,
  dir = "rtl",
  className = "kebab-menu",
  position = "bottom-right",
  animate = true,
  usePortal = true,
  triggerRef = null, // trigger button ref
}) {
  const menuRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});

  // Get enabled actions for keyboard navigation
  const enabledActions = actions.filter((action) => !action.disabled);

  // Close with animation
  const handleClose = () => {
    if (animate) {
      setIsClosing(true);
      setTimeout(() => {
        onClose?.();
        setIsClosing(false);
      }, 200);
    } else {
      onClose?.();
    }
  };

  // Calculate position for portal
  useEffect(() => {
    if (open && usePortal && triggerRef?.current) {
      const trigger = triggerRef.current;
      const rect = trigger.getBoundingClientRect();

      let style = {};

      switch (position) {
        case "bottom-right":
          style = {
            top: rect.bottom + window.scrollY,
            right: window.innerWidth - rect.right - window.scrollX,
          };
          break;
        case "bottom-left":
          style = {
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
          };
          break;
        case "top-right":
          style = {
            bottom: window.innerHeight - rect.top - window.scrollY,
            right: window.innerWidth - rect.right - window.scrollX,
          };
          break;
        case "top-left":
          style = {
            bottom: window.innerHeight - rect.top - window.scrollY,
            left: rect.left + window.scrollX,
          };
          break;
        default:
          style = {
            top: rect.bottom + window.scrollY,
            right: window.innerWidth - rect.right - window.scrollX,
          };
      }

      setMenuStyle(style);
    }
  }, [open, usePortal, triggerRef, position]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!open || enabledActions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < enabledActions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : enabledActions.length - 1
        );
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        const focusedAction = enabledActions[focusedIndex];
        if (focusedAction) {
          onAction?.(focusedAction.key);
          handleClose();
        }
        break;
      case "Tab":
        e.preventDefault();
        if (e.shiftKey) {
          setFocusedIndex((prev) =>
            prev > 0 ? prev - 1 : enabledActions.length - 1
          );
        } else {
          setFocusedIndex((prev) =>
            prev < enabledActions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case "Escape":
        handleClose();
        break;
      default:
        break;
    }
  };

  // Focus management – prevent scroll-jump when focusing first item
  useEffect(() => {
    if (open && menuRef.current) {
      const firstEnabledButton = menuRef.current.querySelector(
        ".kebab-menu-item:not(.disabled)"
      );
      if (firstEnabledButton) {
        try {
          // Avoid scroll jump when focusing
          firstEnabledButton.focus({ preventScroll: true });
        } catch {
          // Fallback for older browsers
          firstEnabledButton.focus();
        }
      }
      setFocusedIndex(0);
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        // Check if click is on the trigger button
        const trigger = e.target.closest(".kebab-trigger, .ccf-menu-trigger");
        if (!trigger) {
          handleClose();
        }
      }
    };

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    // Small delay to prevent immediate close on open
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }, 10);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  if (!open && !isClosing) return null;

  const menuClasses = [
    className,
    `kebab-menu--${position}`,
    animate && "kebab-menu--animated",
    isClosing && "kebab-menu--closing",
    dir === "rtl" && "kebab-menu--rtl",
    usePortal && "kebab-menu--portal",
  ]
    .filter(Boolean)
    .join(" ");

  const menuContent = (
    <div
      ref={menuRef}
      role="menu"
      className={menuClasses}
      dir={dir}
      onKeyDown={handleKeyDown}
      aria-label="Menu options"
      style={usePortal ? menuStyle : {}}
    >
      <div className="kebab-menu-inner">
        {actions.map((action, index) => {
          const isEnabled = !action.disabled;
          const isFocused = enabledActions.indexOf(action) === focusedIndex;

          return (
            <React.Fragment key={action.key}>
              {action.divider && index > 0 && (
                <div className="kebab-menu-divider" />
              )}
              <button
                role="menuitem"
                type="button"
                className={[
                  "kebab-menu-item",
                  action.className,
                  action.disabled && "disabled",
                  isFocused && "focused",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  if (isEnabled) {
                    onAction?.(action.key);
                    handleClose();
                  }
                }}
                onMouseEnter={() => {
                  if (isEnabled) {
                    const enabledIndex = enabledActions.indexOf(action);
                    if (enabledIndex !== -1) {
                      setFocusedIndex(enabledIndex);
                    }
                  }
                }}
                disabled={action.disabled}
                tabIndex={isEnabled ? (isFocused ? 0 : -1) : -1}
                aria-disabled={action.disabled}
              >
                <span className="kebab-menu-label">{action.label}</span>
                {action.shortcut && (
                  <span className="kebab-menu-shortcut">
                    {action.shortcut}
                  </span>
                )}
                {action.Icon && (
                  <span className="kebab-menu-icon" aria-hidden="true">
                    <action.Icon />
                  </span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  // If using portal, render to document.body
  if (usePortal && typeof document !== "undefined") {
    return ReactDOM.createPortal(menuContent, document.body);
  }

  // Otherwise render normally
  return menuContent;
}
